import * as THREE from "three";
import { Tank } from "./Tank";
import { RESPAWN_ZONE_CONFIG } from "./config";

/**
 * Klasa reprezentująca bota AI sterującego czołgiem
 * Bot porusza się w strefie respawnu i atakuje inne czołgi
 */
export class Bot {
  public tank: Tank;
  private scene: THREE.Scene;
  private targetUpdateInterval: number = 500; // ms - szybsze wykrywanie celów
  private lastTargetUpdate: number = 0;
  private currentTarget: Tank | null = null;
  private wanderAngle: number = 0;
  private wanderChangeTime: number = 0;
  private readonly WANDER_CHANGE_INTERVAL: number = 3000; // 3 sekundy
  private readonly MAX_TARGET_DISTANCE: number = 50; // jednostki - większy zasięg wykrywania
  private readonly MAX_TARGET_DISTANCE_SQ: number = this.MAX_TARGET_DISTANCE * this.MAX_TARGET_DISTANCE;
  private readonly SHOOTING_COOLDOWN: number = 400; // ms - szybsze strzelanie
  private lastShootTime: number = 0;
  private readonly RESPAWN_ZONE_SIZE: number = RESPAWN_ZONE_CONFIG.SIZE;
  private readonly RESPAWN_ZONE_HALF_SIZE: number = RESPAWN_ZONE_CONFIG.SIZE / 2;
  private readonly RESPAWN_ZONE_MARGIN: number = 2; // Margines od krawędzi strefy respawnu
  private readonly BOT_SPAWN_DISTANCE: number = this.RESPAWN_ZONE_HALF_SIZE + 15; // Odległość botów od środka (poza strefą respawnu)
  private readonly MIN_CHASE_DISTANCE: number = 5; // Minimalna odległość do celu
  private readonly MAX_CHASE_DISTANCE: number = 20; // Maksymalna odległość do celu
  private readonly MAX_WANDER_DISTANCE: number = this.RESPAWN_ZONE_HALF_SIZE + 45; // Maksymalna odległość wędrówki od środka
  private readonly MOVEMENT_STATES = {
    WANDER: "wander",
    CHASE: "chase",
    RETREAT: "retreat",
    AVOID: "avoid", // Nowy stan do unikania kolizji
  };
  private currentState: string = this.MOVEMENT_STATES.WANDER;
  private stateChangeTime: number = 0;
  private readonly STATE_CHANGE_MIN_INTERVAL: number = 3000; // ms
  private readonly COLLISION_AVOIDANCE_DISTANCE: number = 5; // Minimalna odległość między botami
  private readonly TARGET_PREDICTION_TIME: number = 0.25; // Czas predykcji ruchu celu (sekundy) - krótsza predykcja dla lepszej celności
  private readonly MIN_HEALTH_RETREAT: number = 0.3; // Procent zdrowia przy którym bot zaczyna odwrót
  private shouldStop: boolean = false; // Flaga określająca czy bot powinien się zatrzymać
  // Throttling dla ciężkich operacji (nie dla ruchu!)
  private lastHeavyUpdateTime = 0;
  private readonly HEAVY_UPDATE_INTERVAL = 50; // ms - dla raycastingu i znajdowania celów
  private camera: THREE.Camera | null = null;

  // [NEW] Reusable objects for GC optimization
  private _raycaster = new THREE.Raycaster();
  private _tmpVec1 = new THREE.Vector3();
  private _tmpVec2 = new THREE.Vector3();
  private _targetDirection = new THREE.Vector3();
  private _raycastHits: THREE.Intersection[] = [];

  /**
   * Tworzy nowego bota AI
   * @param scene Scena Three.js
   */
  constructor(scene: THREE.Scene, existingTank?: Tank) {
    /*
    console.log("[Bot] Creating new bot");
    */
    this.scene = scene;
    this.camera = null;

    if (existingTank) {
      this.tank = existingTank;
    } else {
      // Tworzenie czołgu dla bota
      this.tank = new Tank(scene);
      // Ustawienie unikalnego playerId dla bota (używamy UUID zgodnie z systemem identyfikacji graczy)
      this.tank.playerId = `bot-${this.tank.mesh.uuid}`;
    }

    // Ustaw domyślną nazwę bota, aby uniknąć Anonymous
    if (!this.tank.username || this.tank.username === "Anonymous") {
      this.tank.setUsername(`bot_${this.tank.playerId.slice(-4).toLowerCase()}`);
    }

    // Nie dodajemy automatycznie mesha do sceny – zrobi to BotManager / network
    if (!existingTank && this.tank.mesh.parent) {
      this.tank.mesh.parent.remove(this.tank.mesh);
    }

    if (window.gameState?.tanks && !window.gameState.tanks.includes(this.tank)) {
      window.gameState.tanks.push(this.tank);
    }

    const environment = window.gameState?.scene?.userData.environment;
    if (!this.tank.environment && environment) {
      this.tank.setEnvironment(environment);
      /*
      console.log(`[Bot] Environment set for bot: ${this.tank.playerId}`);
      */
    } else if (!environment) {
      console.warn(`[Bot] Could not find environment for bot: ${this.tank.playerId}`);
    }

    // Inicjalizacja pozycji poza strefą respawnu tylko dla lokalnych botów
    if (!existingTank) {
      this.respawnBot();
    }
  }

  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  /**
   * Aktualizuje stan bota w każdej klatce
   * @param deltaTime Czas od ostatniej klatki w sekundach
   * @param tanks Lista wszystkich czołgów w grze
   */
  public update(deltaTime: number, tanks: Tank[]): void {
    const currentTime = Date.now();

    // Jeśli czołg jest zniszczony, poczekaj chwilę przed respawnem
    if (this.tank.isDestroyed) {
      if (currentTime - this.tank.lastDamageTime > 3000) {
        // 3 sekundy opóźnienia
        this.respawnBot();
      }
      return;
    }

    if (!this.tank.isDestroyed) {
      const distanceToCenter = Math.sqrt(
        this.tank.mesh.position.x * this.tank.mesh.position.x + this.tank.mesh.position.z * this.tank.mesh.position.z,
      );

      // Jeśli bot jest krytycznie daleko od centrum, wymuś natychmiastowy teleport
      if (distanceToCenter > this.MAX_WANDER_DISTANCE * 2) {
        /*
        console.log(
          `[Bot] ${
            this.tank.playerId
          } is critically far (${distanceToCenter.toFixed(
            2
          )}), teleporting back`
        );
        */
        this.respawnBot();
        return;
      }

      // Jeśli odległość jest znacząca, wymuś odwrót
      if (distanceToCenter > this.MAX_WANDER_DISTANCE * 1.2) {
        /*
        console.log(
          `[Bot] ${
            this.tank.playerId
          } is too far from center (${distanceToCenter.toFixed(
            2
          )}), forcing retreat`
        );
        */
        this.currentState = this.MOVEMENT_STATES.RETREAT;
        this.currentTarget = null; // Wyczyść cel, żeby się skupić na odwrocie
        this.retreatToSafeZone(deltaTime);
        // Mimo wymuszenia odwrotu, kontynuuj normalną aktualizację
      }
    }

    // Sprawdź odległość od środka
    const distanceToCenter = Math.sqrt(
      this.tank.mesh.position.x * this.tank.mesh.position.x + this.tank.mesh.position.z * this.tank.mesh.position.z,
    );

    // Jeśli bot jest za daleko, wymuś powrót do strefy respawnu
    if (distanceToCenter > this.MAX_WANDER_DISTANCE) {
      this.currentState = this.MOVEMENT_STATES.RETREAT;
    }

    // Znajdź najbliższy cel (throttled - ciężka operacja)
    if (currentTime - this.lastTargetUpdate > this.targetUpdateInterval) {
      this.findTarget(tanks);
      this.lastTargetUpdate = currentTime;
    }

    // Aktualizuj stan ruchu tylko co HEAVY_UPDATE_INTERVAL (nie blokuje ruchu)
    const shouldUpdateHeavy = currentTime - this.lastHeavyUpdateTime > this.HEAVY_UPDATE_INTERVAL;
    if (shouldUpdateHeavy) {
      this.lastHeavyUpdateTime = currentTime;
    }

    // Aktualizuj stan bota
    this.updateMovementState(currentTime);

    switch (this.currentState) {
      case this.MOVEMENT_STATES.WANDER:
        this.wander(currentTime, deltaTime);
        // Strzelaj do celu nawet podczas wędrówki jeśli jest w zasięgu
        if (this.currentTarget && !this.currentTarget.isDestroyed) {
          this.updateAimingAndShooting(deltaTime);
        }
        break;
      case this.MOVEMENT_STATES.CHASE:
        if (this.currentTarget && !this.currentTarget.isDestroyed) {
          // Sprawdź czy cel nie jest za daleko
          const targetDistance = Math.sqrt(
            Math.pow(this.currentTarget.mesh.position.x - this.tank.mesh.position.x, 2) +
              Math.pow(this.currentTarget.mesh.position.z - this.tank.mesh.position.z, 2),
          );

          if (targetDistance > this.MAX_WANDER_DISTANCE) {
            this.currentState = this.MOVEMENT_STATES.WANDER;
            this.currentTarget = null;
            this.wander(currentTime, deltaTime);
          } else {
            this.chaseTarget(deltaTime);
          }
        } else {
          this.currentState = this.MOVEMENT_STATES.WANDER;
          this.wander(currentTime, deltaTime);
        }
        break;
      case this.MOVEMENT_STATES.RETREAT:
        this.retreatToSafeZone(deltaTime);
        // Strzelaj nawet podczas odwrotu jeśli mamy cel
        if (this.currentTarget && !this.currentTarget.isDestroyed) {
          this.updateAimingAndShooting(deltaTime);
        }
        break;
      case this.MOVEMENT_STATES.AVOID:
        this.avoidCollision(deltaTime);
        break;
    }

    // Aktualizuj system ruchu czołgu
    if (!this.tank.isDestroyed) {
      this.tank.update(deltaTime);
    }
  }

  /**
   * Aktualizuje stan ruchu bota
   * @param currentTime Aktualny czas
   */
  private updateMovementState(currentTime: number): void {
    // Jeśli czołg jest zniszczony, nie aktualizuj stanu
    if (this.tank.isDestroyed) {
      return;
    }

    // Zmieniamy stan tylko jeśli minął minimalny interwał
    if (currentTime - this.stateChangeTime < this.STATE_CHANGE_MIN_INTERVAL) {
      return;
    }

    const previousState = this.currentState;

    // Sprawdź odległość od centrum
    const distanceToCenter = Math.sqrt(Math.pow(this.tank.mesh.position.x, 2) + Math.pow(this.tank.mesh.position.z, 2));

    // Sprawdź kolizje z innymi botami
    const nearbyBots =
      window.gameState?.tanks?.filter(
        (tank) =>
          tank !== this.tank &&
          !tank.isDestroyed &&
          this.tank.mesh.position.distanceTo(tank.mesh.position) < this.COLLISION_AVOIDANCE_DISTANCE,
      ) || [];

    // Zmieniamy priorytety stanów - odwrót ma wyższy priorytet
    if (distanceToCenter > this.RESPAWN_ZONE_HALF_SIZE * 1.5) {
      // Jeśli bot jest znacznie poza strefą, wymuś odwrót niezależnie od innych czynników
      this.currentState = this.MOVEMENT_STATES.RETREAT;
    } else if (nearbyBots.length > 0) {
      this.currentState = this.MOVEMENT_STATES.AVOID;
    }
    // Sprawdzenie zdrowia - jeśli niskie, wycofaj się
    else if (this.tank.health < this.tank.maxHealth * this.MIN_HEALTH_RETREAT) {
      this.currentState = this.MOVEMENT_STATES.RETREAT;
    }
    // Jeśli mamy cel i jesteśmy w dobrej kondycji, ścigamy go, ale tylko jeśli nie jesteśmy za daleko
    else if (this.currentTarget && !this.currentTarget.isDestroyed && distanceToCenter < this.MAX_WANDER_DISTANCE) {
      this.currentState = this.MOVEMENT_STATES.CHASE;
    }
    // W przeciwnym razie wędrujemy
    else {
      this.currentState = this.MOVEMENT_STATES.WANDER;
    }

    if (previousState !== this.currentState) {
      this.stateChangeTime = currentTime;
      /*
      console.log(
        `[Bot] ${this.tank.playerId} changed state: ${previousState} -> ${this.currentState}`,
        {
          health: this.tank.health,
          maxHealth: this.tank.maxHealth,
          nearbyBots: nearbyBots.length,
          distanceToCenter: distanceToCenter.toFixed(2),
          hasTarget: !!this.currentTarget,
          targetDestroyed: this.currentTarget?.isDestroyed,
        }
      );
      */
    }
  }

  /**
   * Znajduje najbliższy cel do ataku
   * @param tanks Lista wszystkich czołgów w grze
   */
  private findTarget(tanks: Tank[]): void {
    this.currentTarget = null;

    const botPosition = this.tank.mesh.position;
    const obstacles = this.camera
      ? this.scene.children.filter((obj) => {
          if (!obj.visible) return false;
          if (obj.userData?.isProjectile) return false;
          if (obj.userData?.isTank) return false;
          return obj.type === "Mesh" || obj.type === "Group";
        })
      : null;

    let bestTarget: Tank | null = null;
    let bestScore = -Infinity;

    for (const tank of tanks) {
      if (tank === this.tank || tank.playerId === this.tank.playerId || tank.isDestroyed) {
        continue;
      }

      const distanceSq = botPosition.distanceToSquared(tank.mesh.position);
      if (distanceSq > this.MAX_TARGET_DISTANCE_SQ) continue;
      const distance = Math.sqrt(distanceSq);

      // Oblicz score na podstawie odległości
      let score = this.MAX_TARGET_DISTANCE - distance; // Bliższe cele mają wyższy score

      // Sprawdź czy mamy linię strzału do celu (tylko jeśli mamy kamerę)
      if (obstacles && obstacles.length > 0) {
        // [CHANGED] Use reusable vector
        const direction = this._tmpVec1.subVectors(tank.mesh.position, botPosition).normalize();

        // [CHANGED] Use reusable vector
        const origin = this._tmpVec2.copy(botPosition);
        origin.y += 1; // Avoid creating new Vector3(0, 1, 0)

        // [CHANGED] Use reusable raycaster
        this._raycaster.set(origin, direction);
        this._raycaster.far = distance;
        this._raycastHits.length = 0;
        this._raycaster.intersectObjects(obstacles, true, this._raycastHits);

        // Jeśli cel jest zasłonięty dużą przeszkodą, zmniejsz score (ale nie odrzucaj całkowicie)
        if (this._raycastHits.length > 0 && this._raycastHits[0].distance < distance - 2) {
          score *= 0.3;
        }
      }

      // Preferuj cele z niższym zdrowiem (większy bonus za rannych)
      const healthRatio = tank.health / tank.maxHealth;
      score *= 1.5 + (1 - healthRatio) * 0.5;

      // Bonus za cele które są bliżej (kwadratowy spadek)
      if (distance < 20) {
        score *= 1.3;
      }

      if (score > bestScore) {
        bestScore = score;
        bestTarget = tank;
      }
    }

    this.currentTarget = bestTarget;
  }

  /**
   * Aktualizuje celowanie i strzelanie
   * @param deltaTime Czas od ostatniej klatki
   */
  private updateAimingAndShooting(deltaTime: number): void {
    const currentTime = Date.now();

    if (!this.currentTarget || this.currentTarget.isDestroyed) {
      this.tank.targetTurretAngle = this.tank.mesh.rotation.y;
      return;
    }

    // Bezpośrednia pozycja celu (bez predykcji dla lepszej celności na krótkich dystansach)
    const distanceToTarget = this.tank.mesh.position.distanceTo(this.currentTarget.mesh.position);

    // Użyj predykcji tylko dla dalszych celów
    // [CHANGED] Use reusable objects, predictedPosition stored in _tmpVec2
    if (distanceToTarget > 15 && this.currentTarget.previousPosition) {
      // Przewidywanie ruchu celu dla dalszych dystansów
      // [CHANGED] Use reusable vector for targetVelocity
      const targetVelocity = this._tmpVec1
        .set(
          this.currentTarget.mesh.position.x - this.currentTarget.previousPosition.x,
          0,
          this.currentTarget.mesh.position.z - this.currentTarget.previousPosition.z,
        )
        .multiplyScalar(1 / Math.max(deltaTime, 0.016)); // Zabezpieczenie przed dzieleniem przez małe wartości

      // [CHANGED] Use reusable vector for predictedPosition
      this._tmpVec2
        .copy(this.currentTarget.mesh.position)
        .add(targetVelocity.multiplyScalar(this.TARGET_PREDICTION_TIME));
    } else {
      // Dla bliskich celów - celuj bezpośrednio
      // [CHANGED] Use reusable vector
      this._tmpVec2.copy(this.currentTarget.mesh.position);
    }

    // Obliczamy kierunek do przewidywanej pozycji
    // [CHANGED] Use reusable vector
    const targetDirection = this._targetDirection.subVectors(this._tmpVec2, this.tank.mesh.position).normalize();

    // Kąt do celu w przestrzeni świata (Three.js: -Z to przód, więc atan2(x, z) daje kąt)
    const targetAngleWorld = Math.atan2(targetDirection.x, targetDirection.z);

    // Obracanie całego czołgu w kierunku celu
    const currentTankAngle = this.tank.mesh.rotation.y;
    const angleDifference = this.normalizeAngle(targetAngleWorld - currentTankAngle);

    // Próg obrotu - mniejszy dla lepszej responsywności
    // W Three.js: left zwiększa rotation.y, right zmniejsza rotation.y
    // Gdy angleDiff > 0, cel jest "po lewej" (większy kąt), więc trzeba zwiększyć rotation.y (left)
    if (Math.abs(angleDifference) > 0.03) {
      if (angleDifference > 0) {
        this.tank.stopTurningRight();
        this.tank.startTurningLeft();
      } else {
        this.tank.stopTurningLeft();
        this.tank.startTurningRight();
      }
    } else {
      // Zatrzymaj obrót, gdy czołg jest wystarczająco blisko właściwego kierunku
      this.tank.stopTurningLeft();
      this.tank.stopTurningRight();
    }

    // Strzelanie
    if (currentTime - this.lastShootTime > this.SHOOTING_COOLDOWN) {
      if (!this.tank.turret) {
        return;
      }

      // Oblicz lokalną rotację wieżyczki względem czołgu
      // Wieżyczka musi być obrócona o różnicę między kątem do celu a rotacją czołgu
      const turretLocalAngle = this.normalizeAngle(targetAngleWorld - this.tank.mesh.rotation.y);
      this.tank.turret.rotation.y = turretLocalAngle;

      // Sprawdź czy wieżyczka jest wycelowana (kąt lokalny bliski 0 oznacza że czołg jest skierowany na cel)
      // Strzelaj gdy różnica kątów jest mała
      const aimError = Math.abs(angleDifference);

      // Próg strzału - 0.4 radiana (~23 stopni)
      if (aimError < 0.4) {
        this.tank.shoot(this.scene);
        this.lastShootTime = currentTime;
      }
    }
  }

  /**
   * Tryb wędrówki - losowy ruch w strefie respawnu
   * @param currentTime Aktualny czas
   * @param deltaTime Czas od ostatniej klatki
   */
  private wander(currentTime: number, deltaTime: number): void {
    // Sprawdźmy odległość od środka
    const distanceToCenter = Math.sqrt(Math.pow(this.tank.mesh.position.x, 2) + Math.pow(this.tank.mesh.position.z, 2));

    // Jeśli jesteśmy blisko granicy strefy, wymuś częstszą zmianę kierunku
    if (distanceToCenter > this.RESPAWN_ZONE_HALF_SIZE * 0.8) {
      this.wanderChangeTime = Math.min(this.wanderChangeTime, currentTime + 1000);
    }

    if (currentTime > this.wanderChangeTime) {
      this.stopAllMovement();

      // Oblicz kąt do środka strefy
      const centerAngle = Math.atan2(-this.tank.mesh.position.x, -this.tank.mesh.position.z);

      // Dostosuj losowy kąt w zależności od odległości od środka
      // Im dalej od środka, tym silniejsze przyciąganie do centrum
      const distanceFactor = Math.min(1, distanceToCenter / this.MAX_WANDER_DISTANCE);

      // Zwiększamy "grawitację" w kierunku centrum przy większej odległości
      const centerBias = distanceFactor * 0.75; // Im dalej, tym większy wpływ centrum
      const maxAngleOffset = Math.PI * (1 - distanceFactor * 0.95); // Zmniejszamy zakres losowości
      const randomOffset = (Math.random() - 0.5) * maxAngleOffset;

      // Zastosuj ważone obliczanie kąta, z większą wagą dla centrum przy większej odległości
      this.wanderAngle = centerAngle * centerBias + (centerAngle + randomOffset) * (1 - centerBias);

      // Ustal czas do następnej zmiany - im dalej od centrum, tym częstsze zmiany
      const intervalMultiplier = 1 - distanceFactor * 0.7;
      this.wanderChangeTime = currentTime + this.WANDER_CHANGE_INTERVAL * intervalMultiplier;

      // Dodaj losowe zatrzymywanie się (im bliżej centrum, tym większa szansa)
      this.shouldStop = Math.random() < 0.15 + (1 - distanceFactor) * 0.25;
    }

    this.rotateTankTowards(this.wanderAngle);

    if (!this.tank.isDestroyed) {
      if (this.shouldStop) {
        this.tank.stopMovingForward();

        // Losowo zdecyduj, kiedy zakończyć zatrzymanie
        if (Math.random() < 0.01) {
          this.shouldStop = false;
        }
      } else {
        this.tank.startMovingForward();
      }
    }

    // Dla wersji bez dodatkowych pól klasy, można zastosować wariantową logikę:
    // const randomStopChance = 0.002; // 0.2% szans na zatrzymanie w każdej klatce
    // if (Math.random() < randomStopChance) {
    //   this.tank.stopMovingForward();
    // } else {
    //   this.tank.startMovingForward();
    // }
  }
  /**
   * Tryb pościgu - podążanie za celem
   * @param deltaTime Czas od ostatniej klatki
   */
  private chaseTarget(deltaTime: number): void {
    // sprawdzenie odległości od centrum
    const distanceToCenter = Math.sqrt(Math.pow(this.tank.mesh.position.x, 2) + Math.pow(this.tank.mesh.position.z, 2));

    // Jeśli bot jest zbyt daleko od centrum podczas pościgu, przerwij pościg
    if (distanceToCenter > this.MAX_WANDER_DISTANCE * 0.9) {
      this.currentState = this.MOVEMENT_STATES.RETREAT;
      /*
      console.log(
        `[Bot] ${
          this.tank.playerId
        } stopping chase - too far from center (${distanceToCenter.toFixed(2)})`
      );
      */
      return;
    }

    if (!this.currentTarget || this.currentTarget.isDestroyed) {
      this.currentState = this.MOVEMENT_STATES.WANDER;
      return;
    }

    // Oblicz pozycje w płaszczyźnie XZ
    const targetX = this.currentTarget.mesh.position.x;
    const targetZ = this.currentTarget.mesh.position.z;
    const botX = this.tank.mesh.position.x;
    const botZ = this.tank.mesh.position.z;

    // Oblicz wektor do celu
    const toTargetX = targetX - botX;
    const toTargetZ = targetZ - botZ;

    // Oblicz odległość do celu
    const distanceToTarget = Math.sqrt(toTargetX * toTargetX + toTargetZ * toTargetZ);

    // Oblicz kąt do celu
    const targetAngle = Math.atan2(toTargetX, toTargetZ);

    // Obróć czołg w kierunku celu
    this.rotateTankTowards(targetAngle);

    // Jeśli jesteśmy zbyt blisko celu, wycofaj się
    if (distanceToTarget < this.MIN_CHASE_DISTANCE) {
      this.tank.startMovingBackward();
      this.tank.stopMovingForward();
    }
    // Jeśli jesteśmy zbyt daleko od celu, ruszaj do przodu
    else if (distanceToTarget > this.MAX_CHASE_DISTANCE) {
      this.tank.startMovingForward();
      this.tank.stopMovingBackward();
    }
    // W optymalnej odległości, zatrzymaj ruch
    else {
      this.tank.stopMovingForward();
      this.tank.stopMovingBackward();
    }

    // Aktualizuj celowanie i strzelanie
    this.updateAimingAndShooting(deltaTime);
  }

  /**
   * Tryb odwrotu - powrót do strefy respawnu
   * @param deltaTime Czas od ostatniej klatki
   */
  private retreatToSafeZone(deltaTime: number): void {
    // Oblicz kierunek do centrum
    // [CHANGED] Use reusable vector
    const centerDirection = this._tmpVec1.set(-this.tank.mesh.position.x, 0, -this.tank.mesh.position.z).normalize();

    // Oblicz kąt do centrum
    const centerAngle = Math.atan2(centerDirection.x, centerDirection.z);

    // Obróć czołg w kierunku centrum
    this.rotateTankTowards(centerAngle);

    // Sprawdź czy czołg jest już obrócony w kierunku centrum
    const currentAngle = this.tank.mesh.rotation.y;
    const angleDiff = Math.abs(this.normalizeAngle(currentAngle - centerAngle));

    // Przyspieszony ruch w trybie odwrotu - szybsze obracanie
    const rotationSpeed = Math.min(0.05, angleDiff * 0.2);

    if (angleDiff < 0.5) {
      // Jeśli czołg jest skierowany w stronę centrum, jedź do przodu bardzo szybko
      this.tank.startMovingForward();
      this.tank.stopMovingBackward();
    } else {
      // Jeśli czołg nie jest jeszcze skierowany w stronę centrum, cofaj się
      this.tank.stopMovingForward();
      this.tank.startMovingBackward();
    }

    // Sprawdź czy bot jest już w strefie respawnu
    const distanceToCenter = Math.sqrt(Math.pow(this.tank.mesh.position.x, 2) + Math.pow(this.tank.mesh.position.z, 2));

    if (distanceToCenter <= this.RESPAWN_ZONE_HALF_SIZE * 0.6) {
      // Jeśli bot już jest w strefie respawnu, przywróć normalny tryb
      this.currentState = this.MOVEMENT_STATES.WANDER;
      /*
      console.log(
        `[Bot] ${
          this.tank.playerId
        } returned to safe zone (${distanceToCenter.toFixed(2)})`
      );
      */
    }
  }

  /**
   * Tryb unikania kolizji - unikanie innych botów
   * @param deltaTime Czas od ostatniej klatki
   */
  private avoidCollision(deltaTime: number): void {
    // Oblicz pozycję bota
    const x = this.tank.mesh.position.x;
    const z = this.tank.mesh.position.z;

    // Znajdź najbliższego bota
    const nearbyBots =
      window.gameState?.tanks?.filter(
        (tank) =>
          tank !== this.tank &&
          !tank.isDestroyed &&
          this.tank.mesh.position.distanceTo(tank.mesh.position) < this.COLLISION_AVOIDANCE_DISTANCE,
      ) || [];

    if (nearbyBots.length === 0) {
      this.currentState = this.MOVEMENT_STATES.WANDER;
      return;
    }

    // Oblicz kąt do najbliższego bota
    const closestBot = nearbyBots[0];
    const closestBotX = closestBot.mesh.position.x;
    const closestBotZ = closestBot.mesh.position.z;

    const angleToBot = Math.atan2(closestBotX - x, closestBotZ - z);

    // Obróć czołg w kierunku przeciwnym do bota
    const oppositeAngle = angleToBot + Math.PI;
    this.rotateTankTowards(oppositeAngle);

    // Ruszaj do przodu tylko jeśli czołg nie jest zniszczony
    if (!this.tank.isDestroyed) {
      this.tank.startMovingForward();
    }
  }

  /**
   * Obraca czołg w kierunku docelowego kąta
   * @param targetAngle Docelowy kąt obrotu (w radianach)
   */
  private rotateTankTowards(targetAngle: number): void {
    // Normalizuj kąt docelowy do zakresu -π do π
    targetAngle = this.normalizeAngle(targetAngle);

    // Pobierz aktualną rotację czołgu
    const currentAngle = this.normalizeAngle(this.tank.mesh.rotation.y);

    // Oblicz różnicę kątów
    let angleDiff = this.normalizeAngle(targetAngle - currentAngle);

    // Określ kierunek obrotu
    // W Three.js: left zwiększa rotation.y, right zmniejsza rotation.y
    // Gdy angleDiff > 0, cel jest "po lewej" (większy kąt), więc trzeba zwiększyć rotation.y (left)
    if (Math.abs(angleDiff) > 0.1) {
      // Dodaj próg, aby uniknąć drgań
      if (angleDiff > 0) {
        this.tank.startTurningLeft();
        this.tank.stopTurningRight();
      } else {
        this.tank.startTurningRight();
        this.tank.stopTurningLeft();
      }
    } else {
      // Jeśli jesteśmy wystarczająco blisko celu, zatrzymaj obrót
      this.tank.stopTurningLeft();
      this.tank.stopTurningRight();
    }
  }

  /**
   * Normalizuje kąt do zakresu -π do π
   */
  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= 2 * Math.PI;
    while (angle < -Math.PI) angle += 2 * Math.PI;
    return angle;
  }

  /**
   * Zatrzymuje wszystkie ruchy
   */
  private stopAllMovement(): void {
    // Zatrzymaj wszystkie ruchy
    this.tank.stopMovingForward();
    this.tank.stopMovingBackward();
    this.tank.stopTurningLeft();
    this.tank.stopTurningRight();
  }

  /**
   * Respawnuje bota w losowym miejscu poza strefą respawnu
   */
  private respawnBot(): void {
    /*
    console.log(`[Bot] Respawning bot ${this.tank.playerId}`);
    */

    // Losowy kąt
    const angle = Math.random() * Math.PI * 2;

    // Ustaw pozycję poza strefą respawnu
    this.tank.mesh.position.x = Math.cos(angle) * this.BOT_SPAWN_DISTANCE;
    this.tank.mesh.position.z = Math.sin(angle) * this.BOT_SPAWN_DISTANCE;
    this.tank.mesh.position.y = 0.5; // Wysokość nad ziemią

    // Ustaw rotację w kierunku środka
    this.tank.mesh.rotation.y = angle + Math.PI;

    // Zresetuj zdrowie i inne parametry
    this.tank.health = this.tank.maxHealth;
    this.tank.isDestroyed = false;
    this.tank.damageSmoke = false;

    // Pokaż czołg
    this.tank.mesh.visible = true;
    this.tank.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const material = child.material as THREE.MeshStandardMaterial;
        material.color.setHex(0x5d7f38); // Przywróć oryginalny kolor
        child.visible = true;
      }
    });

    // Zresetuj stan bota
    this.currentState = this.MOVEMENT_STATES.WANDER;
    this.currentTarget = null;
    this.lastTargetUpdate = 0;
    this.wanderChangeTime = 0;

    /*
    console.log(`[Bot] Bot ${this.tank.playerId} respawned at:`, {
      position: this.tank.mesh.position.toArray(),
      rotation: this.tank.mesh.rotation.y,
      health: this.tank.health,
      state: this.currentState,
    });
    */
  }

  /**
   * Tworzy i dodaje boty do gry
   * @param scene Scena Three.js
   * @param count Liczba botów do utworzenia
   * @returns Tablica utworzonych botów
   */
  /**
   * Tworzy określoną liczbę botów
   * @param scene Scena Three.js
   * @param count Liczba botów do utworzenia
   * @returns Tablica utworzonych botów
   */
  public static createBots(scene: THREE.Scene, count: number = 3): Bot[] {
    console.log(`[Bot] Creating ${count} bots`);
    const bots: Bot[] = [];

    for (let i = 0; i < count; i++) {
      const bot = new Bot(scene);
      if (!bot.tank.mesh.parent) {
        scene.add(bot.tank.mesh);
      }
      console.log(`[Bot] Created bot ${i + 1}/${count} with ID: ${bot.tank.playerId}`);
      bots.push(bot);
    }

    console.log(`[Bot] Successfully created ${bots.length} bots`);
    return bots;
  }

  public dispose(): void {
    // Usunięcie listenera eventów i innych referencji
    this.currentTarget = null;

    // Usunięcie pathfinding data
    if (this.tank.mesh.userData.pathfindingData) {
      delete this.tank.mesh.userData.pathfindingData;
    }

    // Usunięcie innych dużych struktur danych
    if (this.tank.mesh.userData.navMesh) {
      delete this.tank.mesh.userData.navMesh;
    }
  }
}
